/**
 * TaxonomyInitProvider Component
 *
 * This component wraps the application and ensures the taxonomy service
 * is properly initialized from the Backend API before rendering children components.
 * Updated for Backend-only approach.
 */
import React, { useState, useEffect } from 'react';
import { initializeTaxonomy } from '../../services/enhancedTaxonomyService';

interface TaxonomyInitProviderProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const TaxonomyInitProvider: React.FC<TaxonomyInitProviderProps> = ({
  children,
  fallback = <div className="taxonomy-loading">Loading taxonomy data...</div>,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);

  const initializeTaxonomyData = async () => {
    try {
      console.log(
        '🚀 [TAXONOMY INIT PROVIDER] Initializing taxonomy from Backend API...'
      );
      await initializeTaxonomy();
      setIsInitialized(true);
      console.log(
        '✅ [TAXONOMY INIT PROVIDER] Taxonomy initialization successful'
      );
    } catch (err) {
      console.error(
        '❌ [TAXONOMY INIT PROVIDER] Taxonomy initialization failed:',
        err
      );
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsInitialized(false);
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    if (!isInitialized && !isRetrying) {
      setIsRetrying(true);
      initializeTaxonomyData();
    }
  }, [isInitialized, isRetrying]);

  if (error) {
    return (
      <div className="taxonomy-error">
        <h3>Error Loading Taxonomy Data</h3>
        <p>{error.message}</p>
        <button
          onClick={() => {
            setError(null);
            setIsRetrying(true);
            initializeTaxonomyData();
          }}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isInitialized) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default TaxonomyInitProvider;
